import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { currencyService } from '../services/currencyService';

export type Currency = 'USD' | 'CAD' | 'GBP';

export interface ExchangeRates {
  USD: number;
  CAD: number;
  GBP: number;
}

export interface CurrencyContextType {
  selectedCurrency: Currency;
  exchangeRates: ExchangeRates;
  isLoading: boolean;
  error: string | null;
  setCurrency: (currency: Currency) => void;
  convertPrice: (price: number, fromCurrency?: Currency) => number;
  formatPrice: (price: number, currency?: Currency) => string;
  getCurrencySymbol: (currency?: Currency) => string;
  getConvertedItemPrice: (usdPrice: number) => number;
  formatDisplayAmount: (amount: number, currency?: Currency) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  CAD: '$',
  GBP: '£'
};

const STORAGE_KEY = 'jampackd-selected-currency';

interface CurrencyProviderProps {
  children: ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('USD');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({
    USD: 1,
    CAD: 1.35,
    GBP: 0.79
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize currency: check for manual preference first, then auto-detect
  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch exchange rates
        const rates = await currencyService.getExchangeRates();
        setExchangeRates(rates);
        
        // Check for manually saved preference first
        const savedCurrency = localStorage.getItem(STORAGE_KEY) as Currency;
        if (savedCurrency && ['USD', 'CAD', 'GBP'].includes(savedCurrency)) {
          setSelectedCurrency(savedCurrency);
        } else {
          // No manual preference, auto-detect from location
          try {
            const detectedCurrency = await currencyService.detectUserLocation();
            setSelectedCurrency(detectedCurrency);
          } catch (err) {
            setSelectedCurrency('USD');
          }
        }
        
      } catch (err) {
        setError('Failed to load current exchange rates');
        // Keep default rates if fetch fails
      } finally {
        setIsLoading(false);
      }
    };

    initializeCurrency();
  }, []);

  const setCurrency = (currency: Currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem(STORAGE_KEY, currency);
  };

  const convertPrice = (price: number, fromCurrency: Currency = 'USD'): number => {
    if (fromCurrency === selectedCurrency) return price;
    
    // Convert from source currency to USD first (if not already USD)
    const usdPrice = fromCurrency === 'USD' ? price : price / exchangeRates[fromCurrency];
    
    // Then convert from USD to target currency
    return selectedCurrency === 'USD' ? usdPrice : usdPrice * exchangeRates[selectedCurrency];
  };

  const formatPrice = (price: number, currency: Currency = selectedCurrency): string => {
    const convertedPrice = convertPrice(price);
    const symbol = getCurrencySymbol(currency);
    
    // Only round up to ceiling dollar for non-USD currencies
    const finalPrice = currency === 'USD' ? convertedPrice : Math.ceil(convertedPrice);
    
    return `${symbol}${finalPrice.toFixed(2)}`;
  };

  // Cart-specific function: Convert USD item price to selected currency with ceiling rounding
  const getConvertedItemPrice = (usdPrice: number): number => {
    const convertedPrice = convertPrice(usdPrice);
    // Apply ceiling rounding for non-USD currencies
    return selectedCurrency === 'USD' ? convertedPrice : Math.ceil(convertedPrice);
  };

  // Cart-specific function: Format already-converted amounts without additional conversion
  const formatDisplayAmount = (amount: number, currency: Currency = selectedCurrency): string => {
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${amount.toFixed(2)}`;
  };

  const getCurrencySymbol = (currency: Currency = selectedCurrency): string => {
    return CURRENCY_SYMBOLS[currency];
  };

  const value: CurrencyContextType = {
    selectedCurrency,
    exchangeRates,
    isLoading,
    error,
    setCurrency,
    convertPrice,
    formatPrice,
    getCurrencySymbol,
    getConvertedItemPrice,
    formatDisplayAmount
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};